<template>
  <div class="affiliates-page">
    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ $t('admin.affiliates.title') }}</h1>
        <p class="page-subtitle">{{ $t('admin.affiliates.subtitle') }}</p>
      </div>
      <div class="header-actions">
        <button class="btn btn-secondary" @click="exportCsv">{{ $t('admin.affiliates.btn_export_csv') }}</button>
      </div>
    </div>

    <!-- Stat Cards -->
    <div class="stat-grid">
      <div class="stat-card">
        <div class="stat-icon"><AppIcon name="affiliate-partners" :size="24" /></div>
        <div class="stat-value">{{ statsLoading ? '—' : stats.totalAffiliates.toLocaleString() }}</div>
        <div class="stat-label">{{ $t('admin.affiliates.stat_total') }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-icon"><AppIcon name="affiliate-verified" :size="24" /></div>
        <div class="stat-value">{{ statsLoading ? '—' : stats.activeAffiliates.toLocaleString() }}</div>
        <div class="stat-label">{{ $t('admin.affiliates.stat_active') }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-icon"><AppIcon name="affiliate-commission" :size="24" /></div>
        <div class="stat-value">{{ statsLoading ? '—' : formatDecimal(stats.totalPaidCommissions) }}</div>
        <div class="stat-label">{{ $t('admin.affiliates.stat_paid_commissions') }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-icon"><AppIcon name="affiliate-referrals" :size="24" /></div>
        <div class="stat-value">{{ statsLoading ? '—' : stats.totalReferrals.toLocaleString() }}</div>
        <div class="stat-label">{{ $t('admin.affiliates.stat_referrals') }}</div>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters-row">
      <select v-model="filters.status" class="filter-select" @change="fetchAffiliates(1)">
        <option value="">{{ $t('admin.affiliates.filter_all_statuses') }}</option>
        <option value="active">{{ $t('admin.affiliates.status_active') }}</option>
        <option value="pending">{{ $t('admin.affiliates.status_pending') }}</option>
        <option value="suspended">{{ $t('admin.affiliates.status_suspended') }}</option>
      </select>

      <select v-model="filters.tier" class="filter-select" @change="fetchAffiliates(1)">
        <option value="">{{ $t('admin.affiliates.filter_all_tiers') }}</option>
        <option value="1">{{ $t('admin.affiliates.tier', { n: 1 }) }}</option>
        <option value="2">{{ $t('admin.affiliates.tier', { n: 2 }) }}</option>
        <option value="3">{{ $t('admin.affiliates.tier', { n: 3 }) }}</option>
        <option value="4">{{ $t('admin.affiliates.tier', { n: 4 }) }}</option>
      </select>

      <div class="search-wrap">
        <input
          v-model="filters.search"
          type="text"
          class="filter-input search-input"
          :placeholder="$t('admin.affiliates.filter_search_placeholder')"
          @keyup.enter="fetchAffiliates(1)"
        >
        <button class="btn-search" @click="fetchAffiliates(1)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Affiliates Table -->
    <div class="table-container">
      <div v-if="loading" class="table-loading">
        <span class="spinner"></span>
        {{ $t('admin.affiliates.loading') }}
      </div>

      <template v-else>
        <table>
          <thead>
            <tr>
              <th>{{ $t('admin.affiliates.col_username') }}</th>
              <th>{{ $t('admin.affiliates.col_code') }}</th>
              <th>{{ $t('admin.affiliates.col_referrals') }}</th>
              <th>{{ $t('admin.affiliates.col_commission') }}</th>
              <th>{{ $t('admin.affiliates.col_pending') }}</th>
              <th>{{ $t('admin.affiliates.col_tier') }}</th>
              <th>{{ $t('admin.affiliates.col_status') }}</th>
              <th>{{ $t('admin.affiliates.col_actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="affiliates.length > 0">
              <tr v-for="affiliate in affiliates" :key="affiliate.id">
                <td><strong class="affiliate-name">{{ affiliate.username }}</strong></td>
                <td class="td-mono">{{ affiliate.tracking_code }}</td>
                <td class="td-mono">{{ affiliate.total_referrals.toLocaleString() }}</td>
                <td class="td-commission">{{ formatDecimal(affiliate.total_commission) }}</td>
                <td class="td-pending">{{ formatCents(affiliate.pending_commission) }}</td>
                <td>
                  <span class="badge badge-tier">{{ $t('admin.affiliates.tier', { n: affiliate.tier }) }}</span>
                </td>
                <td>
                  <span class="badge" :class="statusBadgeClass(affiliate.status)">
                    {{ affiliate.status }}
                  </span>
                </td>
                <td>
                  <div class="action-buttons">
                    <button class="btn-action" title="View Details" @click="openDetail(affiliate.id)">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    </button>
                    <button class="btn-action" title="Edit" @click="openEdit(affiliate.id)">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </template>
            <tr v-else>
              <td colspan="8" class="empty-row">{{ $t('admin.affiliates.empty') }}</td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination -->
        <div v-if="meta.last_page > 1" class="pagination">
          <button
            class="pagination-btn"
            :disabled="meta.current_page <= 1"
            @click="fetchAffiliates(meta.current_page - 1)"
          >
            &larr;
          </button>
          <button
            v-for="page in visiblePages"
            :key="page"
            class="pagination-btn"
            :class="{ active: page === meta.current_page }"
            @click="fetchAffiliates(page)"
          >
            {{ page }}
          </button>
          <span class="pagination-info">
            {{ $t('admin.affiliates.showing', { start: (meta.current_page - 1) * meta.per_page + 1, end: Math.min(meta.current_page * meta.per_page, meta.total), total: meta.total }) }}
          </span>
          <button
            class="pagination-btn"
            :disabled="meta.current_page >= meta.last_page"
            @click="fetchAffiliates(meta.current_page + 1)"
          >
            &rarr;
          </button>
        </div>
      </template>
    </div>

    <!-- Detail Slide-over -->
    <Transition name="slideOver">
      <div v-if="showDetail" class="slide-over-backdrop" @click.self="closeDetail">
        <div class="slide-over">
          <div class="slide-over-header">
            <h2 class="slide-over-title">{{ $t('admin.affiliates.detail_title') }}</h2>
            <button class="btn-close" @click="closeDetail">&times;</button>
          </div>

          <div v-if="detailLoading" class="slide-over-loading">
            <span class="spinner"></span>
            {{ $t('admin.affiliates.loading_details') }}
          </div>

          <div v-else-if="detail" class="slide-over-body">
            <!-- Stats Overview -->
            <div class="detail-section">
              <h3 class="detail-section-title">{{ $t('admin.affiliates.section_overview') }}</h3>
              <div class="detail-grid">
                <div class="detail-item">
                  <span class="detail-label">{{ $t('admin.affiliates.detail_username') }}</span>
                  <span class="detail-value">{{ detail.username }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">{{ $t('admin.affiliates.detail_email') }}</span>
                  <span class="detail-value">{{ detail.email }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">{{ $t('admin.affiliates.detail_tracking_code') }}</span>
                  <span class="detail-value mono">{{ detail.tracking_code }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">{{ $t('admin.affiliates.col_status') }}</span>
                  <span class="detail-value">
                    <span class="badge" :class="statusBadgeClass(detail.status)">{{ detail.status }}</span>
                  </span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">{{ $t('admin.affiliates.col_tier') }}</span>
                  <span class="detail-value">{{ detail.tier }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">{{ $t('admin.affiliates.detail_commission_type') }}</span>
                  <span class="detail-value">{{ commissionTypeLabel(detail.commission_type) }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">{{ $t('admin.affiliates.detail_cpa_amount') }}</span>
                  <span class="detail-value mono">${{ Number(detail.cpa_amount).toFixed(2) }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">{{ $t('admin.affiliates.detail_revenue_share') }}</span>
                  <span class="detail-value mono">{{ Number(detail.revenue_share_percent).toFixed(2) }}%</span>
                </div>
              </div>

              <div class="detail-stats-row">
                <div class="detail-stat">
                  <div class="detail-stat-value">{{ detail.total_clicks.toLocaleString() }}</div>
                  <div class="detail-stat-label">{{ $t('admin.affiliates.stat_clicks') }}</div>
                </div>
                <div class="detail-stat">
                  <div class="detail-stat-value">{{ detail.total_registrations.toLocaleString() }}</div>
                  <div class="detail-stat-label">{{ $t('admin.affiliates.stat_registrations') }}</div>
                </div>
                <div class="detail-stat">
                  <div class="detail-stat-value">{{ detail.total_ftd.toLocaleString() }}</div>
                  <div class="detail-stat-label">{{ $t('admin.affiliates.stat_ftds') }}</div>
                </div>
                <div class="detail-stat">
                  <div class="detail-stat-value">{{ formatDecimal(detail.total_commission_earned) }}</div>
                  <div class="detail-stat-label">{{ $t('admin.affiliates.stat_earned') }}</div>
                </div>
                <div class="detail-stat">
                  <div class="detail-stat-value">{{ formatDecimal(detail.total_commission_paid) }}</div>
                  <div class="detail-stat-label">{{ $t('admin.affiliates.stat_paid') }}</div>
                </div>
                <div class="detail-stat">
                  <div class="detail-stat-value">{{ formatCents(detail.pending_commission) }}</div>
                  <div class="detail-stat-label">{{ $t('admin.affiliates.stat_pending') }}</div>
                </div>
              </div>

              <button class="btn btn-primary payout-btn" @click="openPayout">{{ $t('admin.affiliates.btn_process_payout') }}</button>
            </div>

            <!-- Commission History -->
            <div class="detail-section">
              <h3 class="detail-section-title">{{ $t('admin.affiliates.section_commissions') }}</h3>
              <div v-if="detail.commissions.length === 0" class="detail-empty">{{ $t('admin.affiliates.commissions_empty') }}</div>
              <div v-else class="detail-table-wrap">
                <table class="detail-table">
                  <thead>
                    <tr>
                      <th>{{ $t('admin.affiliates.col_referral') }}</th>
                      <th>{{ $t('admin.affiliates.col_amount') }}</th>
                      <th>{{ $t('admin.affiliates.col_type') }}</th>
                      <th>{{ $t('admin.affiliates.col_status') }}</th>
                      <th>{{ $t('admin.affiliates.col_date') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="c in detail.commissions" :key="c.id">
                      <td>{{ c.referral_username ?? '—' }}</td>
                      <td class="td-commission">${{ (c.amount / 100).toFixed(2) }}</td>
                      <td>{{ c.commission_type }}</td>
                      <td>
                        <span class="badge badge-sm" :class="commStatusClass(c.status)">{{ c.status }}</span>
                      </td>
                      <td class="td-date">{{ formatDate(c.created_at) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Payout History -->
            <div class="detail-section">
              <h3 class="detail-section-title">{{ $t('admin.affiliates.section_payouts') }}</h3>
              <div v-if="detail.payouts.length === 0" class="detail-empty">{{ $t('admin.affiliates.payouts_empty') }}</div>
              <div v-else class="detail-table-wrap">
                <table class="detail-table">
                  <thead>
                    <tr>
                      <th>{{ $t('admin.affiliates.col_amount') }}</th>
                      <th>{{ $t('admin.affiliates.col_status') }}</th>
                      <th>{{ $t('admin.affiliates.col_notes') }}</th>
                      <th>{{ $t('admin.affiliates.col_date') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="p in detail.payouts" :key="p.id">
                      <td class="td-commission">${{ (p.amount / 100).toFixed(2) }}</td>
                      <td>
                        <span class="badge badge-sm" :class="commStatusClass(p.status)">{{ p.status }}</span>
                      </td>
                      <td>{{ p.notes ?? '—' }}</td>
                      <td class="td-date">{{ formatDate(p.created_at) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Referrals -->
            <div class="detail-section">
              <h3 class="detail-section-title">{{ $t('admin.affiliates.section_referrals') }}</h3>
              <div v-if="referralsLoading" class="detail-loading-sm">
                <span class="spinner"></span> {{ $t('admin.affiliates.loading') }}
              </div>
              <div v-else-if="referrals.length === 0" class="detail-empty">{{ $t('admin.affiliates.referrals_empty') }}</div>
              <div v-else class="detail-table-wrap">
                <table class="detail-table">
                  <thead>
                    <tr>
                      <th>{{ $t('admin.affiliates.col_username') }}</th>
                      <th>{{ $t('admin.affiliates.col_registered') }}</th>
                      <th>{{ $t('admin.affiliates.col_status') }}</th>
                      <th>{{ $t('admin.affiliates.col_wagered') }}</th>
                      <th>{{ $t('admin.affiliates.col_commission') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="r in referrals" :key="r.user_id">
                      <td>{{ r.username }}</td>
                      <td class="td-date">{{ formatDate(r.registered_at) }}</td>
                      <td>
                        <span class="badge badge-sm" :class="statusBadgeClass(r.status)">{{ r.status }}</span>
                      </td>
                      <td class="td-mono">${{ Number(r.total_wagered).toFixed(2) }}</td>
                      <td class="td-commission">${{ (r.commission_earned / 100).toFixed(2) }}</td>
                    </tr>
                  </tbody>
                </table>

                <div v-if="referralMeta.last_page > 1" class="pagination pagination-sm">
                  <button
                    class="pagination-btn"
                    :disabled="referralMeta.current_page <= 1"
                    @click="fetchReferrals(referralMeta.current_page - 1)"
                  >
                    &larr;
                  </button>
                  <span class="pagination-info">
                    {{ $t('admin.affiliates.page_of', { current: referralMeta.current_page, total: referralMeta.last_page }) }}
                  </span>
                  <button
                    class="pagination-btn"
                    :disabled="referralMeta.current_page >= referralMeta.last_page"
                    @click="fetchReferrals(referralMeta.current_page + 1)"
                  >
                    &rarr;
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Edit Modal -->
    <Transition name="modal">
      <div v-if="showEdit" class="modal-backdrop" @click.self="closeEdit">
        <div class="modal">
          <div class="modal-header">
            <h2 class="modal-title">{{ $t('admin.affiliates.edit_modal_title') }}</h2>
            <button class="btn-close" @click="closeEdit">&times;</button>
          </div>
          <div v-if="editLoading" class="modal-loading">
            <span class="spinner"></span> {{ $t('admin.affiliates.loading') }}
          </div>
          <form v-else class="modal-body" @submit.prevent="submitEdit">
            <div class="form-group">
              <label class="form-label">{{ $t('admin.affiliates.field_commission_type') }}</label>
              <select v-model="editForm.commission_type" class="form-input">
                <option value="cpa">{{ $t('admin.affiliates.commission_cpa') }}</option>
                <option value="revenue_share">{{ $t('admin.affiliates.commission_revenue_share') }}</option>
                <option value="hybrid">{{ $t('admin.affiliates.commission_hybrid') }}</option>
              </select>
            </div>
            <div class="form-group">
              <label class="form-label">{{ $t('admin.affiliates.field_cpa_amount') }}</label>
              <input v-model="editForm.cpa_amount" type="number" step="0.01" min="0" class="form-input">
            </div>
            <div class="form-group">
              <label class="form-label">{{ $t('admin.affiliates.field_revenue_share') }}</label>
              <input v-model="editForm.revenue_share_percent" type="number" step="0.01" min="0" max="100" class="form-input">
            </div>
            <div class="form-group">
              <label class="form-label">{{ $t('admin.affiliates.col_tier') }}</label>
              <input v-model.number="editForm.tier" type="number" min="1" class="form-input">
            </div>
            <div class="form-group">
              <label class="form-label">{{ $t('admin.affiliates.col_status') }}</label>
              <select v-model="editForm.status" class="form-input">
                <option value="active">{{ $t('admin.affiliates.status_active') }}</option>
                <option value="pending">{{ $t('admin.affiliates.status_pending') }}</option>
                <option value="suspended">{{ $t('admin.affiliates.status_suspended') }}</option>
              </select>
            </div>
            <div class="modal-actions">
              <button type="button" class="btn btn-secondary" @click="closeEdit">{{ $t('admin.affiliates.btn_cancel') }}</button>
              <button type="submit" class="btn btn-primary" :disabled="editSaving">
                {{ editSaving ? $t('admin.affiliates.btn_saving') : $t('admin.affiliates.btn_save_changes') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Transition>

    <!-- Payout Modal -->
    <Transition name="modal">
      <div v-if="showPayout" class="modal-backdrop" @click.self="closePayout">
        <div class="modal">
          <div class="modal-header">
            <h2 class="modal-title">{{ $t('admin.affiliates.payout_modal_title') }}</h2>
            <button class="btn-close" @click="closePayout">&times;</button>
          </div>
          <form class="modal-body" @submit.prevent="submitPayout">
            <p class="payout-info">
              {{ $t('admin.affiliates.payout_affiliate') }}: <strong>{{ detail?.username }}</strong><br>
              {{ $t('admin.affiliates.payout_pending') }}: <strong>{{ detail ? formatCents(detail.pending_commission) : '$0.00' }}</strong>
            </p>
            <div class="form-group">
              <label class="form-label">{{ $t('admin.affiliates.field_payout_amount') }}</label>
              <input v-model="payoutForm.amount" type="number" step="0.01" min="0.01" class="form-input" required>
            </div>
            <div class="form-group">
              <label class="form-label">{{ $t('admin.affiliates.field_payout_notes') }}</label>
              <input v-model="payoutForm.notes" type="text" class="form-input" :placeholder="$t('admin.affiliates.field_payout_notes_placeholder')">
            </div>
            <div class="modal-actions">
              <button type="button" class="btn btn-secondary" @click="closePayout">{{ $t('admin.affiliates.btn_cancel') }}</button>
              <button type="submit" class="btn btn-primary" :disabled="payoutSaving">
                {{ payoutSaving ? $t('admin.affiliates.btn_processing') : $t('admin.affiliates.btn_confirm_payout') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import AppIcon from '@/components/AppIcon.vue'
import apiClient from '@/api/client'
import { useToastStore } from '@/stores/toast'

// ---------------------------------------------------------------
// Types
// ---------------------------------------------------------------

interface AffiliateListItem {
  id: number
  user_id: number
  username: string
  email: string
  tracking_code: string
  total_referrals: number
  total_commission: string
  pending_commission: number
  commission_type: string
  tier: number
  status: string
  created_at: string | null
}

interface CommissionRecord {
  id: number
  referral_username: string | null
  amount: number
  commission_type: string
  status: string
  paid_at: string | null
  created_at: string | null
}

interface PayoutRecord {
  id: number
  amount: number
  status: string
  processed_at: string | null
  notes: string | null
  created_at: string | null
}

interface AffiliateDetail {
  id: number
  user_id: number
  username: string
  email: string
  tracking_code: string
  commission_type: string
  cpa_amount: string
  revenue_share_percent: string
  tier: number
  status: string
  total_clicks: number
  total_registrations: number
  total_ftd: number
  total_commission_earned: string
  total_commission_paid: string
  pending_commission: number
  created_at: string | null
  commissions: CommissionRecord[]
  payouts: PayoutRecord[]
}

interface ReferralItem {
  user_id: number
  username: string
  registered_at: string | null
  status: string
  total_wagered: string
  commission_earned: number
}

interface PaginationMeta {
  current_page: number
  last_page: number
  per_page: number
  total: number
}

interface Stats {
  totalAffiliates: number
  activeAffiliates: number
  totalPaidCommissions: string
  totalReferrals: number
}

// ---------------------------------------------------------------
// State
// ---------------------------------------------------------------

const { t } = useI18n()
const toast = useToastStore()

const affiliates = ref<AffiliateListItem[]>([])
const loading = ref(false)
const meta = ref<PaginationMeta>({ current_page: 1, last_page: 1, per_page: 15, total: 0 })

const stats = ref<Stats>({ totalAffiliates: 0, activeAffiliates: 0, totalPaidCommissions: '0', totalReferrals: 0 })
const statsLoading = ref(false)

const filters = ref({ status: '', tier: '', search: '' })

// Detail slide-over
const showDetail = ref(false)
const detail = ref<AffiliateDetail | null>(null)
const detailLoading = ref(false)
const currentDetailId = ref<number | null>(null)

// Referrals within detail
const referrals = ref<ReferralItem[]>([])
const referralsLoading = ref(false)
const referralMeta = ref<PaginationMeta>({ current_page: 1, last_page: 1, per_page: 15, total: 0 })

// Edit modal
const showEdit = ref(false)
const editLoading = ref(false)
const editSaving = ref(false)
const editForm = ref({
  commission_type: 'revenue_share',
  cpa_amount: '0',
  revenue_share_percent: '0',
  tier: 1,
  status: 'active',
})
const editAffiliateId = ref<number | null>(null)

// Payout modal
const showPayout = ref(false)
const payoutSaving = ref(false)
const payoutForm = ref({ amount: '', notes: '' })

// ---------------------------------------------------------------
// Computed
// ---------------------------------------------------------------

const visiblePages = computed(() => {
  const pages: number[] = []
  const total = meta.value.last_page
  const current = meta.value.current_page
  const start = Math.max(1, current - 2)
  const end = Math.min(total, current + 2)
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  return pages
})

// ---------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------

function formatCents(cents: number): string {
  return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatDecimal(value: string): string {
  const num = Number(value)
  return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'active':
      return 'badge-active'
    case 'pending':
      return 'badge-pending'
    case 'suspended':
      return 'badge-suspended'
    default:
      return 'badge-pending'
  }
}

function commStatusClass(status: string): string {
  switch (status) {
    case 'paid':
    case 'completed':
      return 'badge-active'
    case 'pending':
      return 'badge-pending'
    default:
      return 'badge-pending'
  }
}

function commissionTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    cpa: t('admin.affiliates.commission_cpa'),
    revenue_share: t('admin.affiliates.commission_revenue_share'),
    hybrid: t('admin.affiliates.commission_hybrid'),
  }
  return labels[type] ?? type
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const date = new Date(iso)
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

// ---------------------------------------------------------------
// Data fetching
// ---------------------------------------------------------------

async function fetchAffiliates(page: number): Promise<void> {
  loading.value = true

  try {
    const params: Record<string, string | number> = { page, per_page: 15 }
    if (filters.value.status) params.status = filters.value.status
    if (filters.value.tier) params.tier = filters.value.tier
    if (filters.value.search) params.search = filters.value.search

    const response = await apiClient.get('/admin/api/affiliates', { params })
    affiliates.value = response.data.data as AffiliateListItem[]
    meta.value = {
      current_page: response.data.current_page,
      last_page: response.data.last_page,
      per_page: response.data.per_page,
      total: response.data.total,
    }
  } catch {
    toast.error(t('admin.affiliates.error_load'))
  } finally {
    loading.value = false
  }
}

async function fetchStats(): Promise<void> {
  statsLoading.value = true

  try {
    // Fetch total + active counts from the list endpoint
    const [allRes, activeRes] = await Promise.all([
      apiClient.get('/admin/api/affiliates', { params: { per_page: 1 } }),
      apiClient.get('/admin/api/affiliates', { params: { status: 'active', per_page: 1 } }),
    ])

    // Aggregate from list data
    let totalPaid = 0
    let totalReferrals = 0

    // Fetch a larger set to compute aggregates
    const fullRes = await apiClient.get('/admin/api/affiliates', { params: { per_page: 100 } })
    const items = fullRes.data.data as AffiliateListItem[]
    for (const item of items) {
      totalPaid += Number(item.total_commission)
      totalReferrals += item.total_referrals
    }

    stats.value = {
      totalAffiliates: allRes.data.total as number,
      activeAffiliates: activeRes.data.total as number,
      totalPaidCommissions: totalPaid.toFixed(8),
      totalReferrals,
    }
  } catch {
    toast.error(t('admin.affiliates.error_load_stats'))
  } finally {
    statsLoading.value = false
  }
}

async function fetchDetail(id: number): Promise<void> {
  detailLoading.value = true
  currentDetailId.value = id

  try {
    const response = await apiClient.get(`/admin/api/affiliates/${id}`)
    detail.value = response.data as AffiliateDetail
  } catch {
    toast.error(t('admin.affiliates.error_load_detail'))
    showDetail.value = false
  } finally {
    detailLoading.value = false
  }
}

async function fetchReferrals(page: number): Promise<void> {
  if (!currentDetailId.value) return
  referralsLoading.value = true

  try {
    const response = await apiClient.get(`/admin/api/affiliates/${currentDetailId.value}/referrals`, {
      params: { page, per_page: 15 },
    })
    referrals.value = response.data.data as ReferralItem[]
    referralMeta.value = {
      current_page: response.data.current_page,
      last_page: response.data.last_page,
      per_page: response.data.per_page,
      total: response.data.total,
    }
  } catch {
    toast.error(t('admin.affiliates.error_load_referrals'))
  } finally {
    referralsLoading.value = false
  }
}

// ---------------------------------------------------------------
// Detail slide-over
// ---------------------------------------------------------------

function openDetail(id: number): void {
  showDetail.value = true
  fetchDetail(id)
  fetchReferralsForAffiliate(id)
}

function closeDetail(): void {
  showDetail.value = false
  detail.value = null
  referrals.value = []
  currentDetailId.value = null
}

function fetchReferralsForAffiliate(id: number): void {
  currentDetailId.value = id
  fetchReferrals(1)
}

// ---------------------------------------------------------------
// Edit modal
// ---------------------------------------------------------------

async function openEdit(id: number): Promise<void> {
  editAffiliateId.value = id
  showEdit.value = true
  editLoading.value = true

  try {
    const response = await apiClient.get(`/admin/api/affiliates/${id}`)
    const data = response.data as AffiliateDetail
    editForm.value = {
      commission_type: data.commission_type,
      cpa_amount: data.cpa_amount,
      revenue_share_percent: data.revenue_share_percent,
      tier: data.tier,
      status: data.status,
    }
  } catch {
    toast.error(t('admin.affiliates.error_load_detail'))
    showEdit.value = false
  } finally {
    editLoading.value = false
  }
}

function closeEdit(): void {
  showEdit.value = false
  editAffiliateId.value = null
}

async function submitEdit(): Promise<void> {
  if (!editAffiliateId.value) return
  editSaving.value = true

  try {
    await apiClient.put(`/admin/api/affiliates/${editAffiliateId.value}`, {
      commission_type: editForm.value.commission_type,
      cpa_amount: Number(editForm.value.cpa_amount),
      revenue_share_percent: Number(editForm.value.revenue_share_percent),
      tier: editForm.value.tier,
      status: editForm.value.status,
    })
    toast.success(t('admin.affiliates.toast_updated'))
    closeEdit()
    fetchAffiliates(meta.value.current_page)
    // Refresh detail if open
    if (showDetail.value && currentDetailId.value === editAffiliateId.value) {
      fetchDetail(currentDetailId.value)
    }
  } catch {
    toast.error(t('admin.affiliates.error_update'))
  } finally {
    editSaving.value = false
  }
}

// ---------------------------------------------------------------
// Payout modal
// ---------------------------------------------------------------

function openPayout(): void {
  payoutForm.value = { amount: '', notes: '' }
  showPayout.value = true
}

function closePayout(): void {
  showPayout.value = false
}

async function submitPayout(): Promise<void> {
  if (!currentDetailId.value) return
  payoutSaving.value = true

  try {
    await apiClient.post(`/admin/api/affiliates/${currentDetailId.value}/payout`, {
      amount: Number(payoutForm.value.amount),
      notes: payoutForm.value.notes || undefined,
    })
    toast.success(t('admin.affiliates.toast_payout'))
    closePayout()
    // Refresh detail to show updated balances
    fetchDetail(currentDetailId.value)
    fetchAffiliates(meta.value.current_page)
  } catch {
    toast.error(t('admin.affiliates.error_payout'))
  } finally {
    payoutSaving.value = false
  }
}

// ---------------------------------------------------------------
// Export CSV (simple client-side)
// ---------------------------------------------------------------

function exportCsv(): void {
  if (affiliates.value.length === 0) {
    toast.warning(t('admin.affiliates.export_empty'))
    return
  }

  const header = 'Username,Code,Referrals,Total Commission,Pending Commission,Tier,Status'
  const rows = affiliates.value.map((a) =>
    [a.username, a.tracking_code, a.total_referrals, a.total_commission, (a.pending_commission / 100).toFixed(2), a.tier, a.status].join(','),
  )

  const csv = [header, ...rows].join('\n')
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'affiliates.csv'
  link.click()
  URL.revokeObjectURL(url)
}

// ---------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------

onMounted(() => {
  fetchAffiliates(1)
  fetchStats()
})
</script>

<style scoped>
.affiliates-page {
  padding: 24px;
  animation: fadeIn 0.3s ease both;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Page Header */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 4px;
}

.page-subtitle {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 10px;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: var(--radius-md, 8px);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-family: inherit;
  transition: all 150ms ease;
}

.btn-primary {
  background: var(--accent-primary, #00e701);
  color: #000;
  letter-spacing: 0.02em;
}

.btn-primary:hover {
  background: #00cc01;
  box-shadow: 0 0 24px rgba(0, 231, 1, 0.35);
  transform: translateY(-1px);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.btn-secondary {
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-secondary:hover {
  color: var(--text-primary, #fff);
  border-color: rgba(255, 255, 255, 0.2);
}

/* Stat Grid */
.stat-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  padding: 20px;
  transition: all 150ms ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  border-color: rgba(255, 255, 255, 0.1);
}

.stat-icon {
  font-size: 24px;
  margin-bottom: 12px;
}

.stat-value {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin-bottom: 4px;
}

.stat-label {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* Filters */
.filters-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
}

.filter-select,
.filter-input {
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  padding: 9px 12px;
  font-size: 13px;
  font-family: inherit;
}

.filter-select option {
  background: var(--bg-secondary, #1a2c38);
}

.filter-select:focus,
.filter-input:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.search-wrap {
  display: flex;
}

.search-input {
  border-radius: var(--radius-md, 8px) 0 0 var(--radius-md, 8px);
  width: 220px;
}

.btn-search {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-left: none;
  border-radius: 0 var(--radius-md, 8px) var(--radius-md, 8px) 0;
  color: var(--text-secondary, #b1bad3);
  cursor: pointer;
  transition: color 150ms ease;
}

.btn-search:hover {
  color: var(--accent-primary, #00e701);
}

/* Table */
.table-container {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  overflow: hidden;
}

.table-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 48px 20px;
  color: var(--text-secondary, #b1bad3);
  font-size: 14px;
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead {
  background: var(--bg-tertiary, #213743);
}

th {
  padding: 12px 16px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-tertiary, #7f8fa4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  white-space: nowrap;
}

td {
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
}

tbody tr {
  background: var(--bg-secondary, #1a2c38);
  transition: background 150ms ease;
}

tbody tr:hover {
  background: #253d4f;
}

tbody tr:last-child td {
  border-bottom: none;
}

.affiliate-name {
  font-weight: 600;
  color: var(--text-primary, #fff);
}

.td-mono {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  color: var(--text-secondary, #b1bad3);
}

.td-commission {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-weight: 600;
  color: #ffd700;
}

.td-pending {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  color: var(--color-success, #00e701);
}

.td-date {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  white-space: nowrap;
}

/* Badges */
.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  border-radius: var(--radius-full, 9999px);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  white-space: nowrap;
}

.badge-sm {
  padding: 2px 8px;
  font-size: 10px;
}

.badge-active {
  background: rgba(0, 231, 1, 0.15);
  color: var(--color-success, #00e701);
}

.badge-pending {
  background: rgba(255, 167, 38, 0.15);
  color: var(--color-warning, #ffa726);
}

.badge-suspended {
  background: rgba(237, 66, 69, 0.15);
  color: var(--color-danger, #ed4245);
}

.badge-tier {
  background: rgba(20, 117, 225, 0.15);
  color: #5ba7f0;
}

/* Action buttons */
.action-buttons {
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-action {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md, 8px);
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 150ms ease;
  color: var(--text-tertiary, #7f8fa4);
}

.btn-action:hover {
  background: rgba(0, 231, 1, 0.15);
  color: var(--accent-primary, #00e701);
  border-color: rgba(0, 231, 1, 0.3);
}

/* Empty row */
.empty-row {
  text-align: center;
  padding: 48px 20px !important;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 14px;
}

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.pagination-sm {
  padding: 12px 0;
  border-top: none;
}

.pagination-btn {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md, 8px);
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 150ms ease;
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
}

.pagination-btn:hover:not(:disabled):not(.active) {
  color: var(--text-primary, #fff);
  border-color: rgba(255, 255, 255, 0.2);
}

.pagination-btn.active {
  background: var(--accent-primary, #00e701);
  color: #000;
  border-color: var(--accent-primary, #00e701);
}

.pagination-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pagination-info {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0 12px;
}

/* Spinner */
.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Slide-over */
.slide-over-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 500;
  display: flex;
  justify-content: flex-end;
}

.slide-over {
  width: 600px;
  max-width: 90vw;
  background: var(--bg-primary, #0f1923);
  border-left: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.slide-over-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
}

.slide-over-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0;
}

.slide-over-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 48px 20px;
  color: var(--text-secondary, #b1bad3);
}

.slide-over-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.btn-close {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md, 8px);
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-secondary, #b1bad3);
  font-size: 18px;
  transition: all 150ms ease;
}

.btn-close:hover {
  color: var(--text-primary, #fff);
  border-color: rgba(255, 255, 255, 0.2);
}

/* Detail sections */
.detail-section {
  margin-bottom: 28px;
}

.detail-section-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 0 0 14px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.detail-label {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.detail-value {
  font-size: 14px;
  color: var(--text-primary, #fff);
  font-weight: 500;
}

.detail-value.mono {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
}

.detail-stats-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.detail-stat {
  background: var(--bg-secondary, #1a2c38);
  border-radius: var(--radius-md, 8px);
  padding: 12px;
  text-align: center;
}

.detail-stat-value {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin-bottom: 2px;
}

.detail-stat-label {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.payout-btn {
  width: 100%;
  margin-top: 8px;
}

.detail-empty {
  text-align: center;
  padding: 24px;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 13px;
}

.detail-loading-sm {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
}

.detail-table-wrap {
  overflow-x: auto;
}

.detail-table {
  width: 100%;
  border-collapse: collapse;
}

.detail-table thead {
  background: var(--bg-tertiary, #213743);
}

.detail-table th {
  padding: 8px 12px;
  font-size: 10px;
}

.detail-table td {
  padding: 10px 12px;
  font-size: 12px;
}

/* Modal */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-lg, 12px);
  width: 480px;
  max-width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.modal-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0;
}

.modal-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 48px 20px;
  color: var(--text-secondary, #b1bad3);
}

.modal-body {
  padding: 24px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

/* Form */
.form-group {
  margin-bottom: 16px;
}

.form-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary, #b1bad3);
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.form-input {
  width: 100%;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  padding: 10px 12px;
  font-size: 14px;
  font-family: inherit;
}

.form-input:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.form-input option {
  background: var(--bg-secondary, #1a2c38);
}

.payout-info {
  font-size: 14px;
  color: var(--text-secondary, #b1bad3);
  line-height: 1.6;
  margin: 0 0 16px;
}

.payout-info strong {
  color: var(--text-primary, #fff);
}

/* Transitions */
.slideOver-enter-active,
.slideOver-leave-active {
  transition: opacity 0.2s ease;
}

.slideOver-enter-active .slide-over,
.slideOver-leave-active .slide-over {
  transition: transform 0.25s ease;
}

.slideOver-enter-from,
.slideOver-leave-to {
  opacity: 0;
}

.slideOver-enter-from .slide-over {
  transform: translateX(100%);
}

.slideOver-leave-to .slide-over {
  transform: translateX(100%);
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-active .modal,
.modal-leave-active .modal {
  transition: transform 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal {
  transform: scale(0.95);
}

.modal-leave-to .modal {
  transform: scale(0.95);
}

/* Responsive */
@media (max-width: 1200px) {
  .stat-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .affiliates-page {
    padding: 16px;
  }

  .stat-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .filters-row {
    flex-direction: column;
  }

  .search-input {
    width: 100%;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }

  .detail-stats-row {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
